import { definitionReaders, type DefinitionRow, type DefinitionReaders } from './location-type-ambience';
import { fromBaseRecord } from '../compile/from-base-context';
import { readItemUuidSlot } from '../compile/inventory-raw-readers';
import { isPhpNumeric, phpJsonFloat } from '../compile/php-decimal-values';
import { phpJsonTruthy } from '../compile/php-json-values';
import { constructPlayerSaveSectionMedia } from '../compile/from-base-player-media';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';

/** Account facts are delivered once per source. Visibility and selectable rows belong to this caller. */
export function searchCandidate(row: DefinitionRow, viewer: DefinitionRow, sources: DefinitionRow, creature: boolean, read: DefinitionReaders = definitionReaders) {
    const field = (key: string) => read.item({ items: row }, key);
    const id = String(field('item_id') ?? ''), type = String(field('item_type') ?? '');
    const sourceId = String(field('source_id') ?? '');
    const account = fromBaseRecord(sources[sourceId]) ? sources[sourceId] : {};
    const viewerId = String(viewer.id ?? '');
    const owner = viewerId !== '' && String(field('item_created_by_id') ?? '').toUpperCase() === viewerId.toUpperCase();
    const visible = !phpJsonTruthy(field('item_deleted')) && !phpJsonTruthy(field('item_archived'))
        && (!phpJsonTruthy(field('item_private')) || !creature && (viewer.staff === true || owner));
    // Both admitted pickers contain only ObjectType inventory or creature definitions, which the table license covers.
    const enabled = sourceId === '' || account.na === true || account.personal === true || account.table === true;
    return { id, type, sourceId, account, visible, enabled };
}

export function searchDefinitionRow(candidate: DefinitionRow, host: DefinitionRow, viewer: DefinitionRow, sources: DefinitionRow,
    creature: boolean, root: string, subscriptionUrl = '', read: DefinitionReaders = definitionReaders) {
    const state = searchCandidate(candidate, viewer, sources, creature, read);
    const label = decodeHtmlEntitiesForPlainText(read.item(host, 'item_name'));
    const sourceLabel = decodeHtmlEntitiesForPlainText(read.item({ items: candidate }, 'source_name'));
    const sourceUrl = state.sourceId && !state.account.na ? `${root}source.php?source_id=${encodeURIComponent(state.sourceId)}` : '';
    const thumbnail = { item_id: state.id, name: label, w: creature ? 44 : 40, h: creature ? 44 : 40, link: creature,
        imgClasses: 'rounded', pictures: Object.fromEntries(['item', 'weapon_classification', 'weapon_ammo_classification',
            'armor_classification', 'instrument_classification', 'armor_type'].map(key => [key, window.ftPicturesOf(host, key)])) };
    const base = { id: state.id, label, type: state.type, type_label: state.type.replace(/_/g, ' ').replace(/\b\w/g, text => text.toUpperCase()),
        url: `${root}item.php?id=${encodeURIComponent(state.id)}`, icon: { id: state.type },
        action: { kind: creature ? 'add_encounter_creature' : 'add_inventory', enabled: state.enabled, reason_code: state.enabled ? null : 'source_locked' },
        source: { id: state.sourceId, label: sourceUrl ? sourceLabel : '', url: sourceUrl }, thumbnail };
    if (creature) {
        const subtype = readItemUuidSlot(read.layer(host, 'creature_sub_type'));
        const catalog = fromBaseRecord(host.catalog) ? host.catalog : {};
        const reference = Object.entries(catalog).find(([id]) => id.toUpperCase() === subtype.toUpperCase())?.[1];
        const rawCr = read.layer(host, 'cr');
        return { ...base, size: readItemUuidSlot(read.layer(host, 'size')), creature_type: readItemUuidSlot(read.layer(host, 'creature_type')),
            creature_sub_type: subtype, creature_sub_type_label: reference ? decodeHtmlEntitiesForPlainText(read.item(reference, 'item_name')) : '',
            cr: isPhpNumeric(rawCr) && Number.isFinite(phpJsonFloat(rawCr)) ? phpJsonFloat(rawCr) : null };
    }
    const price = Number(String(read.item({ items: candidate }, 'source_price') ?? '').replace(/,/g, ''));
    return { ...base, purchase: state.enabled || !sourceUrl ? null : state.account.subscription === true
        ? { label: 'Get Plus', url: subscriptionUrl } : state.account.purchasable === true
            ? { label: price > 0 ? `Buy $${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Buy', url: sourceUrl } : null };
}

export async function searchDefinitionResults(value: unknown, creature: boolean, onlyOwned: boolean) {
    if (!fromBaseRecord(value) || value.shape_v !== 2 || !Array.isArray(value.results)) throw new Error('Search inputs unavailable. Please retry.');
    // Empty queries are deliberately cheap and carry no definition package.
    if (!value.results.length) return { shape_v: 1, renderer: value.renderer, results: [] };
    if (!fromBaseRecord(value.viewer) || !fromBaseRecord(value.sources) || typeof value.root !== 'string') throw new Error('Search account inputs unavailable. Please retry.');
    const viewer = value.viewer, sources = value.sources, root = value.root;
    const candidates = value.results.filter(fromBaseRecord).filter(row => {
        const state = searchCandidate(row, viewer, sources, creature);
        return state.visible && (!onlyOwned || state.enabled);
    });
    // Keyword cards read saved identity/facts and their own gallery. They must not join the
    // scene's serial compiler queue; the Add action obtains the full compile closure separately.
    if (!fromBaseRecord(value.definitions)) throw new Error('Search definitions unavailable. Please retry.');
    const rawDefinitions = fromBaseRecord(value.definitions) ? value.definitions : {};
    const metadata = fromBaseRecord(value.catalog) && fromBaseRecord(value.catalog.metadata) ? value.catalog.metadata : {};
    const cdnUrl = typeof value.cdn_url === 'string' ? value.cdn_url : typeof metadata.cdnUrl === 'string' ? metadata.cdnUrl : undefined;
    const results = candidates.map(row => {
        const id = String(definitionReaders.item({ items: row }, 'item_id'));
        const raw = rawDefinitions[id.toUpperCase()];
        const host = fromBaseRecord(raw)
            ? { ...constructPlayerSaveSectionMedia(raw, cdnUrl, definitionReaders.layer), catalog: rawDefinitions }
            : null;
        if (!host) throw new Error('Search definition unavailable. Please retry.');
        return searchDefinitionRow(row, host, viewer, sources, creature, root, String(value.subscription_url ?? ''));
    });
    return { shape_v: 1, renderer: value.renderer, results };
}

const MAX_SEARCH_DEFINITION_PAGES = 4;

/** Bound refill requests after browser eligibility; SQL never selects game visibility for this viewer. */
export async function searchDefinitionPages(readPage: (offset: number) => Promise<unknown>, creature: boolean, onlyOwned: boolean, current: () => boolean) {
    const limit = creature ? 20 : 50, results: DefinitionRow[] = [];
    let offset = 0;
    for (let pageNumber = 0; pageNumber < MAX_SEARCH_DEFINITION_PAGES && current(); pageNumber++) {
        const raw = await readPage(offset);
        if (!current()) break;
        const page = await searchDefinitionResults(raw, creature, onlyOwned);
        if (!current()) break;
        results.push(...page.results);
        const next = fromBaseRecord(raw) ? raw.next_offset : null;
        if (results.length >= limit || next === null || next === undefined || pageNumber + 1 === MAX_SEARCH_DEFINITION_PAGES)
            return { ...page, results: results.slice(0, limit) };
        if (!Number.isInteger(next) || Number(next) <= offset || Number(next) > 100000)
            throw new Error('Search continuation unavailable. Please narrow the search or retry.');
        offset = Number(next);
    }
    return null;
}
